import { Client, Office, Opportunity, User } from '../types';
import { OpportunityInput } from '../services/opportunitiesService';
import { TABLE_FONT_FAMILY } from '../theme';
import { clientPocsWithEmail } from './clientPocs';
import {
  OPPORTUNITY_STATUS_FILTERS,
  getOpportunityStatusFilterLabel,
  getOpportunityStatusLabel,
  isTerminalOpportunityStatus,
  type OpportunityStatusFilter,
} from './commercialPlanning';
import { opportunityToInput } from './opportunityForm';
import { latestNotesCommentPreview } from './opportunityNotes';

const COLUMN_CELL_PADDING = 28;
const ACTION_ICON_BUTTON_WIDTH = 30;
const ACTION_BUTTON_GAP = 4;
const ACTION_ROW_PADDING_RIGHT = 4;
const ACTION_TEXT_BUTTON_PADDING_X = 16;
const ACTION_BUTTON_FONT_PX = 11.2;
export const LEAD_ACTIONS_MAX_WIDTH = 360;

export const CREATE_PROPOSAL_STATUSES = new Set(['bidding', 'negotiation', 'won']);

export type LeadActionId =
  | 'createProposal'
  | 'openProposal'
  | 'createProject'
  | 'createLead'
  | 'nextStep'
  | 'edit'
  | 'delete';

export const LEAD_ACTION_LABELS: Record<LeadActionId, string> = {
  createProposal: 'Create Proposal',
  openProposal: 'Open Proposal',
  createProject: 'Create Project',
  createLead: 'Create Lead',
  nextStep: '',
  edit: '',
  delete: '',
};

type LeadActionOpportunity = Pick<
  Opportunity,
  'status' | 'proposal_project_id' | 'awarded_project_id'
>;

export function leadActionIds(
  opportunity: LeadActionOpportunity | undefined,
  canCreateProject: boolean,
): LeadActionId[] {
  if (!opportunity) return [];

  const ids: LeadActionId[] = [];
  if (
    canCreateProject
    && CREATE_PROPOSAL_STATUSES.has(opportunity.status)
    && !opportunity.proposal_project_id
  ) {
    ids.push('createProposal');
  }
  if (opportunity.proposal_project_id) {
    ids.push('openProposal');
  }
  if (
    canCreateProject
    && opportunity.status === 'won'
    && !opportunity.awarded_project_id
  ) {
    ids.push('createProject');
  }
  if (opportunity.status === 'lost') {
    ids.push('createLead');
  }
  if (!isTerminalOpportunityStatus(opportunity.status)) {
    ids.push('nextStep');
  }
  ids.push('edit', 'delete');
  return ids;
}

function canUseCanvasTextMetrics(): boolean {
  return typeof document !== 'undefined'
    && typeof navigator !== 'undefined'
    && !/jsdom/i.test(navigator.userAgent);
}

function measureActionLabelWidth(text: string): number {
  const label = text || '';
  const fallback = label.length * 7;
  if (!canUseCanvasTextMetrics()) {
    return fallback;
  }

  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return fallback;
    const primaryFont = TABLE_FONT_FAMILY.split(',')[0].replace(/"/g, "'");
    ctx.font = `600 ${ACTION_BUTTON_FONT_PX}px ${primaryFont}, sans-serif`;
    return ctx.measureText(label).width;
  } catch {
    return fallback;
  }
}

function actionControlWidth(id: LeadActionId): number {
  const label = LEAD_ACTION_LABELS[id];
  if (!label) return ACTION_ICON_BUTTON_WIDTH;
  return Math.ceil(measureActionLabelWidth(label) + ACTION_TEXT_BUTTON_PADDING_X);
}

function leadActionsWidthForIds(ids: LeadActionId[]): number {
  if (!ids.length) {
    return COLUMN_CELL_PADDING;
  }
  const controls = ids.reduce((sum, id) => sum + actionControlWidth(id), 0);
  const gaps = ACTION_BUTTON_GAP * Math.max(0, ids.length - 1);
  return Math.ceil(controls + gaps + ACTION_ROW_PADDING_RIGHT + COLUMN_CELL_PADDING);
}

export function leadActionsColumnWidth(
  rows: Array<{ opportunity?: LeadActionOpportunity }>,
  canCreateProject: boolean,
): number {
  const fallback = leadActionsWidthForIds(['nextStep', 'edit', 'delete']);
  return rows.reduce((widest, row) => {
    const width = leadActionsWidthForIds(leadActionIds(row.opportunity, canCreateProject));
    return Math.max(widest, width);
  }, fallback);
}

export type OpportunityGridColumnKey =
  | 'title'
  | 'status'
  | 'client'
  | 'commercial_owner'
  | 'office'
  | 'next_action_health'
  | 'next_action_date'
  | 'last_note'
  | 'actions';

export type { OpportunityStatusFilter };

export const OPPORTUNITY_GRID_COLUMNS: OpportunityGridColumnKey[] = [
  'title',
  'status',
  'client',
  'commercial_owner',
  'office',
  'next_action_health',
  'next_action_date',
  'last_note',
  'actions',
];

export const OPPORTUNITY_COLUMN_LABELS: Record<OpportunityGridColumnKey, string> = {
  title: 'Title',
  status: 'Status',
  client: 'Client',
  commercial_owner: 'Owner',
  office: 'Office',
  next_action_health: '',
  next_action_date: 'Next Action',
  last_note: 'Last Note',
  actions: '',
};

export const OPPORTUNITY_COLUMN_PICKER_LABELS: Record<
  Exclude<OpportunityGridColumnKey, 'actions'>,
  string
> = {
  title: 'Title',
  status: 'Status',
  client: 'Client',
  commercial_owner: 'Owner',
  office: 'Office',
  next_action_health: 'Health',
  next_action_date: 'Next Action',
  last_note: 'Last Note',
};

export const LEADS_TABLE_STATE_KEY = 'leads_table_state_v1';
export const LEADS_TABLE_HIDDEN_COLUMNS_KEY = 'leads_table_hidden_columns_v1';
export const LEADS_DEFAULT_HIDDEN_COLUMNS: OpportunityGridColumnKey[] = ['office'];

export { OPPORTUNITY_STATUS_FILTERS, getOpportunityStatusFilterLabel };

export const LEADS_PAGE_PATH = '/sales/leads';

const LEADS_SEARCH_PARAMS = {
  status: 'status',
  query: 'q',
  leadId: 'lead',
} as const;

export type LeadsUrlFilters = {
  status: OpportunityStatusFilter;
  query: string;
  leadId: string | null;
};

export function readLeadsUrlFilters(params: URLSearchParams): LeadsUrlFilters {
  const status = params.get(LEADS_SEARCH_PARAMS.status);
  return {
    status: OPPORTUNITY_STATUS_FILTERS.find((filter) => filter === status) ?? 'all',
    query: params.get(LEADS_SEARCH_PARAMS.query) ?? '',
    leadId: params.get(LEADS_SEARCH_PARAMS.leadId)?.trim() || null,
  };
}

/** Apply filter changes to a copy of `params`; empty values and the `all` status drop their key. */
export function writeLeadsUrlFilters(
  params: URLSearchParams,
  changes: Partial<LeadsUrlFilters>,
): URLSearchParams {
  const next = new URLSearchParams(params);
  (Object.keys(changes) as (keyof LeadsUrlFilters)[]).forEach((key) => {
    const value = changes[key];
    if (!value || (key === 'status' && value === 'all')) {
      next.delete(LEADS_SEARCH_PARAMS[key]);
    } else {
      next.set(LEADS_SEARCH_PARAMS[key], value);
    }
  });
  return next;
}

export function leadsPageUrl(filters: Partial<LeadsUrlFilters> = {}): string {
  const search = writeLeadsUrlFilters(new URLSearchParams(), filters).toString();
  return search ? `${LEADS_PAGE_PATH}?${search}` : LEADS_PAGE_PATH;
}

export function leadRowMatchesLeadId(row: LeadGridRow, leadId: string | null): boolean {
  return !leadId || String(row.opportunity?.id ?? '') === leadId;
}

export type NextActionHealth = 'green' | 'yellow' | 'red' | 'grey' | 'none';

export const NEXT_ACTION_HEALTH_LABELS: Record<Exclude<NextActionHealth, 'none'>, string> = {
  green: 'On track (2+ days out)',
  yellow: 'Due today or tomorrow',
  red: 'Overdue',
  grey: 'No next action',
};

export function nextActionHealth(value: unknown, status?: string): NextActionHealth {
  const formatted = formatOpportunityDate(value);
  if (!formatted) return status === 'other' ? 'grey' : 'none';
  const delta = daysUntil(formatted);
  if (delta === null) return status === 'other' ? 'grey' : 'none';
  if (delta >= 2) return 'green';
  if (delta >= 0) return 'yellow';
  return 'red';
}

function daysUntil(value?: string): number | null {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((date.getTime() - today.getTime()) / 86400000);
}

export function formatOpportunityDate(value: unknown): string {
  if (value == null || value === '') return '';
  if (value instanceof Date) return value.toISOString().split('T')[0];
  const str = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  const parsed = new Date(str);
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().split('T')[0];
  return str;
}

export function formatNextActionCell(value: unknown): string {
  const formatted = formatOpportunityDate(value);
  if (!formatted) return '';
  const delta = daysUntil(formatted);
  return delta !== null ? `${formatted} (${delta}d)` : formatted;
}

export function leadRowMatchesQuickFilter(
  row: LeadGridRow,
  query: string,
  lookup: {
    clients: Client[];
    salesUsers: User[];
    offices: Office[];
  },
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;

  const client = lookup.clients.find((item) => item.id === row.client_id);
  const kindLabel = client
    ? ((client.kind ?? 'client') === 'lead' ? 'Lead' : 'Client')
    : '';
  const clientText = client
    ? `${client.name} (${kindLabel})`
    : (row.opportunity?.client_name || '');

  const owner =
    row.commercial_owner_name
    || lookup.salesUsers.find((user) => user.id === row.commercial_owner_id)?.name
    || '';

  const office = lookup.offices.find((item) => item.id === row.office_id);
  const officeText = [
    row.office_name,
    office?.name,
    office?.code,
    office?.country,
  ].filter(Boolean).join(' ');

  const health = nextActionHealth(row.next_action_date, row.status);
  const healthText = health === 'none' ? '' : NEXT_ACTION_HEALTH_LABELS[health];

  const haystack = [
    row.title,
    row.status,
    getOpportunityStatusLabel(row.status),
    clientText,
    owner,
    officeText,
    healthText,
    formatNextActionCell(row.next_action_date),
    latestNotesCommentPreview(row.opportunity?.notes || ''),
  ]
    .join(' ')
    .toLowerCase();

  return haystack.includes(needle);
}

export function measureTableTextWidth(text: string, fontWeight = 400): number {
  const label = text || '';
  if (typeof document === 'undefined') {
    return label.length * 8 + COLUMN_CELL_PADDING;
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return label.length * 8 + COLUMN_CELL_PADDING;
  }

  const primaryFont = TABLE_FONT_FAMILY.split(',')[0].replace(/"/g, "'");
  ctx.font = `${fontWeight} 13px ${primaryFont}, monospace`;
  return ctx.measureText(label).width + COLUMN_CELL_PADDING;
}

export function columnWidthFromContent(
  header: string,
  cellTexts: string[],
  min = 72,
  max = 360,
): number {
  const widths = [
    measureTableTextWidth(header, 700),
    ...cellTexts.map((text) => measureTableTextWidth(text, 400)),
  ];
  return Math.min(max, Math.max(min, Math.max(...widths, 0)));
}

export { opportunityToInput };

export function getFirstPocContactEmail(client: Client): string {
  return clientPocsWithEmail(client)[0]?.email || '';
}

export type LeadGridRowKind = 'lead';

export type LeadGridRow = {
  id: string;
  rowKind: LeadGridRowKind;
  title: string;
  status: string;
  client_id?: number;
  commercial_owner_id?: number;
  commercial_owner_name?: string;
  office_id?: number;
  office_name?: string;
  next_action_date?: string;
  opportunity?: Opportunity;
};

export function isUndatedOtherLead(row: Pick<LeadGridRow, 'status' | 'next_action_date'>): boolean {
  return row.status === 'other' && !formatOpportunityDate(row.next_action_date);
}

export function sortLeadGridRows(rows: LeadGridRow[]): LeadGridRow[] {
  return [...rows].sort((a, b) => Number(isUndatedOtherLead(a)) - Number(isUndatedOtherLead(b)));
}

export function opportunityToLeadRow(opportunity: Opportunity): LeadGridRow {
  return {
    id: `lead-${opportunity.id}`,
    rowKind: 'lead',
    title: opportunity.title,
    status: opportunity.status,
    client_id: opportunity.client_id,
    commercial_owner_id: opportunity.commercial_owner_id,
    commercial_owner_name: opportunity.commercial_owner_name,
    office_id: opportunity.office_id,
    office_name: opportunity.office_name,
    next_action_date: opportunity.next_action_date,
    opportunity,
  };
}

export function buildLeadGridRows(opportunities: Opportunity[]): LeadGridRow[] {
  return opportunities.map(opportunityToLeadRow);
}

export function leadRowToOpportunity(row: LeadGridRow): Opportunity | null {
  if (row.rowKind !== 'lead' || !row.opportunity) return null;
  return {
    ...row.opportunity,
    title: row.title,
    status: row.opportunity.status,
    client_id: row.client_id,
    commercial_owner_id: row.commercial_owner_id,
    commercial_owner_name: row.commercial_owner_name,
    office_id: row.office_id,
    office_name: row.office_name,
    next_action_date: row.next_action_date,
  };
}
